import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";

function FieldLabel({ htmlFor, required, tooltip, children }: { htmlFor?: string; required?: boolean; tooltip: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 mb-1">
      <Label htmlFor={htmlFor} className="mb-0">
        {children}{required && " *"}
      </Label>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-sm">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

interface GuaranteedIssueTabProps {
  formData: Record<string, any>;
  updateField: (field: string, value: any) => void;
  planType: string;
}

export function GuaranteedIssueTab({ formData, updateField }: GuaranteedIssueTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="eoiRequired"
          checked={formData.eoiRequired || false}
          onCheckedChange={(checked) => updateField("eoiRequired", checked)}
        />
        <div className="flex items-center gap-1.5">
          <Label htmlFor="eoiRequired" className="cursor-pointer mb-0">
            Evidence of Insurability (EOI) Required
          </Label>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-sm">
                When enabled, employees requesting coverage above the Guaranteed Issue limit must submit medical evidence to the carrier for approval.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <FieldLabel htmlFor="guaranteedIssueAmount" tooltip="The maximum coverage amount automatically approved without medical underwriting for existing employees.">Standard GI Amount</FieldLabel>
          <Input
            id="guaranteedIssueAmount"
            type="number"
            step="1000"
            value={formData.guaranteedIssueAmount || ""}
            onChange={(e) => updateField("guaranteedIssueAmount", e.target.value)}
            placeholder="100000"
          />
          <p className="text-xs text-muted-foreground mt-1">General GI limit</p>
        </div>
        <div>
          <FieldLabel htmlFor="guaranteedIssueNewHire" tooltip="Higher GI limit for newly hired employees enrolling within their initial eligibility window. Typically more generous than standard GI.">New Hire GI</FieldLabel>
          <Input
            id="guaranteedIssueNewHire"
            type="number"
            step="1000"
            value={formData.guaranteedIssueNewHire || ""}
            onChange={(e) => updateField("guaranteedIssueNewHire", e.target.value)}
            placeholder="150000"
          />
          <p className="text-xs text-muted-foreground mt-1">For newly eligible</p>
        </div>
        <div>
          <FieldLabel htmlFor="guaranteedIssueOpenEnrollment" tooltip="GI limit during the annual open enrollment period. Often lower than new hire GI since employees had a prior opportunity to enroll.">Open Enrollment GI</FieldLabel>
          <Input
            id="guaranteedIssueOpenEnrollment"
            type="number"
            step="1000"
            value={formData.guaranteedIssueOpenEnrollment || ""}
            onChange={(e) => updateField("guaranteedIssueOpenEnrollment", e.target.value)}
            placeholder="50000"
          />
          <p className="text-xs text-muted-foreground mt-1">During OE period</p>
        </div>
      </div>

      <Separator />

      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel htmlFor="increaseAllowance" tooltip="How much an already-enrolled employee can increase their coverage at open enrollment without EOI. Example: $10K means they can bump up one increment per year.">Increase Allowance at OE</FieldLabel>
          <Input
            id="increaseAllowance"
            type="number"
            step="1000"
            value={formData.increaseAllowance || ""}
            onChange={(e) => updateField("increaseAllowance", e.target.value)}
            placeholder="10000"
          />
          <p className="text-xs text-muted-foreground mt-1">
            GI increase allowance during open enrollment
          </p>
        </div>
        <div>
          <FieldLabel htmlFor="maximumWithoutEOI" tooltip="The absolute ceiling for coverage that can be obtained without medical underwriting, regardless of how the employee qualifies (new hire, OE, etc.).">Maximum Without EOI</FieldLabel>
          <Input
            id="maximumWithoutEoi"
            type="number"
            step="1000"
            value={formData.maximumWithoutEoi || ""}
            onChange={(e) => updateField("maximumWithoutEoi", e.target.value)}
            placeholder="500000"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Absolute GI ceiling regardless of eligibility window
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="alwaysRequireEoiAtOe"
          checked={formData.alwaysRequireEoiAtOe || false}
          onCheckedChange={(checked) => updateField("alwaysRequireEoiAtOe", checked)}
        />
        <div className="flex items-center gap-1.5">
          <Label htmlFor="alwaysRequireEoiAtOe" className="cursor-pointer mb-0">
            Always Require EOI at Open Enrollment
          </Label>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-sm">
                Late entrants — employees who declined coverage initially and are enrolling for the first time at OE — must provide EOI regardless of the amount requested.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div>
        <FieldLabel htmlFor="eoiRequiredAfterAmount" tooltip="Any coverage election above this dollar amount triggers an EOI requirement, even within the GI window. Acts as a hard EOI threshold.">EOI Required After Amount</FieldLabel>
        <Input
          id="eoiRequiredAfterAmount"
          type="number"
          step="1000"
          value={formData.eoiRequiredAfterAmount || ""}
          onChange={(e) => updateField("eoiRequiredAfterAmount", e.target.value)}
          placeholder="100000"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Amount threshold that triggers EOI requirement
        </p>
      </div>

      <Separator />

      <div>
        <Label htmlFor="eoiCarrierPortalUrl">Carrier EOI Portal URL</Label>
        <Input
          id="eoiCarrierPortalUrl"
          type="url"
          value={formData.eoiCarrierPortalUrl || ""}
          onChange={(e) => updateField("eoiCarrierPortalUrl", e.target.value)}
          placeholder="https://carrier.com/eoi"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Link to carrier's EOI submission portal
        </p>
      </div>
    </div>
  );
}
